import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { ArrowRight, Loader2, XCircle, Sparkles, Gift } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BtnLink, Card } from "@/components/kit";
import { API, useApi } from "@/lib/api";
import { GiftCodeRow } from "@/pages/Gift";

function Shell({ children, testId }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-ivory px-5 py-10" data-testid={testId}>
      <div className="mb-8"><Logo /></div>
      <Card className="w-full max-w-md text-center">{children}</Card>
    </div>
  );
}

const SPARKS = Array.from({ length: 14 }, (_, i) => ({
  left: `${(i * 37) % 100}%`,
  delay: `${(i % 7) * 0.18}s`,
  size: 6 + (i % 4) * 3,
  dur: `${2.4 + (i % 5) * 0.35}s`,
}));

function Celebration({ children, testId }) {
  return (
    <div className="grain relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-bx px-5 py-12 text-white" data-testid={testId}>
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        {SPARKS.map((s, i) => (
          <span key={i} className="ugc-spark absolute bottom-[-20px] rounded-full bg-gold"
            style={{ left: s.left, width: s.size, height: s.size, animationDelay: s.delay, animationDuration: s.dur }} />
        ))}
        <div className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-gold/20 ugc-ring" />
        <div className="absolute left-1/2 top-1/2 h-[360px] w-[360px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-gold/10 ugc-ring [animation-delay:0.4s]" />
      </div>
      <div className="relative z-10 mb-10"><Logo light /></div>
      <div className="stagger relative z-10 mx-auto max-w-xl text-center">{children}</div>
    </div>
  );
}

function WelcomeSelf() {
  return (
    <Celebration testId="payment-success">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 ring-1 ring-gold/60">
        <Sparkles className="h-7 w-7 text-gold" strokeWidth={1.5} />
      </span>
      <p className="mt-6 text-xs font-semibold uppercase tracking-[0.3em] text-gold">Paiement confirmé</p>
      <h1 className="mt-4 text-4xl font-semibold leading-tight sm:text-5xl" data-testid="payment-welcome-title">
        Bienvenue dans <em className="font-medium text-gold">UGC GO</em> !
      </h1>
      <p className="mt-5 text-lg text-white/85">Ton parcours « Ma première vidéo UGC » est débloqué. Ta première mission t'attend : comprendre l'UGC en quelques minutes.</p>
      <div className="mt-9 flex flex-col items-center gap-4">
        <BtnLink to="/mission/1" variant="light" size="lg" className="group whitespace-nowrap" data-testid="payment-go-mission1">
          Je commence la mission 1<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </BtnLink>
        <Link to="/parcours" className="text-sm text-white/75 underline-offset-4 hover:text-white hover:underline" data-testid="payment-go-dashboard">Voir mon tableau de bord</Link>
      </div>
      <p className="mt-10 text-xs text-white/55">Un email de bienvenue vient de t'être envoyé.</p>
    </Celebration>
  );
}

function WelcomeGift({ sessionId }) {
  const { isSignedIn } = useAuth();
  const api = useApi();
  const [gift, setGift] = useState(null);
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!isSignedIn) return setGaveUp(true);
    let tries = 0;
    let t;
    const load = () => api("GET", "/gifts").then((r) => {
      const g = r.gifts.find((x) => x.session_id === sessionId);
      if (g) setGift(g);
      else if (++tries < 10) t = setTimeout(load, 1500);
      else setGaveUp(true);
    }).catch(() => setGaveUp(true));
    load();
    return () => clearTimeout(t);
  }, [isSignedIn, api, sessionId]);
  return (
    <Celebration testId="payment-gift-success">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 ring-1 ring-gold/60">
        <Gift className="h-7 w-7 text-gold" strokeWidth={1.5} />
      </span>
      <p className="mt-6 text-xs font-semibold uppercase tracking-[0.3em] text-gold">Merci pour ton cadeau</p>
      <h1 className="mt-4 text-4xl font-semibold leading-tight sm:text-5xl">Ton cadeau est prêt !</h1>
      <p className="mt-5 text-lg text-white/85">Transmets ce code à ton amie. Elle crée son compte sur UGC GO, puis le saisit sur la page d'accès.</p>
      <div className="mx-auto mt-8 max-w-sm text-left text-ink">
        {gift ? <ul><GiftCodeRow g={gift} /></ul>
          : gaveUp ? <p className="text-center text-white/80" data-testid="gift-code-fallback">Ton code arrive dans quelques instants : retrouve-le dans ton email ou sur la page « Mes codes cadeaux ».</p>
          : <p className="flex items-center justify-center gap-2 text-white/80"><Loader2 className="h-4 w-4 animate-spin" />Préparation du code…</p>}
      </div>
      <p className="mt-6 text-sm text-white/70">Le code t'a aussi été envoyé par email. Tu le retrouves à tout moment sur la page « Offrir ».</p>
      <BtnLink to="/offrir" variant="light" size="lg" className="mt-8 whitespace-nowrap" data-testid="payment-go-gifts">Mes codes cadeaux</BtnLink>
    </Celebration>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [kind, setKind] = useState("self");

  useEffect(() => {
    if (!sessionId) return setStatus("error");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await (await fetch(`${API}/payments/status/${sessionId}`)).json();
        if (r.payment_status === "paid") {
          setKind(r.kind || "self");
          return setStatus("paid");
        }
        if (r.payment_status === "expired" || r.payment_status === "failed") return setStatus("error");
      } catch {}
      if (++tries >= 15) return setStatus("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  if (status === "checking")
    return (
      <Shell testId="payment-checking">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-bx" />
        <p className="mt-4 text-ink">Confirmation de ton paiement…</p>
      </Shell>
    );
  if (status === "paid") return kind === "gift" ? <WelcomeGift sessionId={sessionId} /> : <WelcomeSelf />;
  return (
    <Shell testId="payment-error">
      <XCircle className="mx-auto h-12 w-12 text-bx-2" strokeWidth={1.4} />
      <h1 className="mt-4 text-2xl font-semibold text-bx">{status === "timeout" ? "Paiement en cours de vérification" : "Le paiement n'a pas abouti"}</h1>
      <p className="mt-3 text-soft">{status === "timeout" ? "La confirmation prend un peu plus de temps que prévu. Actualise cette page dans quelques instants." : "Aucun montant n'a été débité. Tu peux réessayer quand tu veux."}</p>
      <BtnLink to="/acces" size="lg" className="mt-6 w-full" data-testid="payment-retry">Retour</BtnLink>
    </Shell>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const gift = params.get("gift") === "1";
  return (
    <Shell testId="payment-cancel">
      <h1 className="text-2xl font-semibold text-bx">Paiement annulé</h1>
      <p className="mt-3 text-soft">Aucun montant n'a été débité. {gift ? "Tu peux offrir le parcours quand tu le souhaites." : "Ton compte est prêt : tu peux débloquer ton parcours quand tu le souhaites."}</p>
      <BtnLink to={gift ? "/offrir" : "/acces"} size="lg" className="mt-6 w-full" data-testid="payment-cancel-back">Revenir à l'offre</BtnLink>
    </Shell>
  );
}
